import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { allNotices as mockAllNotices } from "@/mocks/noticesPageData";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";

interface Notice {
  id: number;
  title: string;
  content: string;
  is_important: boolean;
  views: number;
  created_at: string;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const ITEMS_PER_PAGE = 10;

export default function NoticesPage() {
  const [notices, setNotices] = useState<Notice[]>(mockAllNotices);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const idParam = searchParams.get("id");
    if (idParam) {
      const noticeId = Number(idParam);
      if (!Number.isNaN(noticeId)) {
        setExpandedId(noticeId);
        setCurrentPage(1);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;

    async function fetchNotices() {
      try {
        const { data, error: fetchError } = await supabase
          .from("notices")
          .select("*")
          .order("created_at", { ascending: false });

        if (!cancelled && data && !fetchError) {
          setNotices(data as Notice[]);
        }
      } catch {
        if (!cancelled) {
          setError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchNotices();
    return () => {
      cancelled = true;
    };
  }, []);

  const totalPages = Math.ceil(notices.length / ITEMS_PER_PAGE);
  const paginatedNotices = notices.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setExpandedId(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <div className="max-w-[860px] mx-auto px-3 md:px-4 py-10 flex items-center justify-center">
          <div className="flex items-center gap-2 text-foreground-600">
            <span className="w-4 h-4 flex items-center justify-center animate-spin">
              <i className="ri-loader-4-line" />
            </span>
            <span className="text-sm">불러오는 중...</span>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <div className="max-w-[860px] mx-auto px-3 md:px-4 py-10">
          <div className="text-center py-10">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
              <span className="w-8 h-8 flex items-center justify-center text-foreground-600">
                <i className="ri-error-warning-line text-2xl" />
              </span>
            </div>
            <h2 className="text-lg font-bold text-foreground-700 mb-2">데이터를 불러올 수 없습니다</h2>
            <p className="text-sm text-foreground-600 mb-4">잠시 후 다시 시도해주세요</p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-foreground-50 text-sm font-bold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              새로고침
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      <div className="max-w-[860px] mx-auto px-3 md:px-4 py-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-400 transition-colors"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line" />
              </span>
              홈으로
            </Link>
          </div>
          <div className="flex items-center gap-3 mt-3">
            <span className="w-8 h-8 flex items-center justify-center text-primary-500">
              <i className="ri-megaphone-line text-xl" />
            </span>
            <h1 className="text-xl md:text-2xl font-bold text-foreground-700">공지사항</h1>
            <span className="px-2 py-0.5 text-[10px] font-bold bg-primary-500/15 text-primary-400 rounded whitespace-nowrap">
              총 {notices.length}건
            </span>
          </div>
          <p className="text-sm text-foreground-600 mt-1.5 ml-11">
            월드컵티비 시즌2의 최신 소식과 중요 안내사항을 확인하세요
          </p>
        </div>

        {/* Notices List */}
        <div className="flex flex-col gap-2">
          {paginatedNotices.map((notice) => (
            <div
              key={notice.id}
              className="bg-background-100 rounded-lg border border-background-300 overflow-hidden transition-colors"
            >
              <button
                onClick={() =>
                  setExpandedId(expandedId === notice.id ? null : notice.id)
                }
                className="w-full flex items-start gap-3 px-4 md:px-5 py-3.5 md:py-4 text-left cursor-pointer"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {notice.is_important ? (
                    <span className="px-1.5 py-0.5 text-[9px] font-bold bg-red-500/15 text-red-400 rounded flex-shrink-0">
                      중요
                    </span>
                  ) : (
                    <span className="w-10 flex-shrink-0 text-[11px] font-bold text-foreground-600 text-center">
                      {notice.id}
                    </span>
                  )}
                  <span className="flex-1 text-sm text-foreground-700 font-medium truncate">
                    {notice.title}
                  </span>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-[11px] text-foreground-600 hidden sm:block">
                      {formatDate(notice.created_at)}
                    </span>
                    <span className="text-[11px] text-foreground-600 hidden sm:flex items-center gap-1">
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-eye-line text-[10px]" />
                      </span>
                      {notice.views}
                    </span>
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i
                        className={`ri-arrow-down-s-line text-foreground-600 transition-transform text-sm ${
                          expandedId === notice.id ? "rotate-180" : ""
                        }`}
                      />
                    </span>
                  </div>
                </div>
              </button>

              {expandedId === notice.id && (
                <div className="px-4 md:px-5 pb-4 md:pb-5">
                  <div className="pt-3 border-t border-background-300/50">
                    <p className="text-sm text-foreground-600 leading-relaxed whitespace-pre-wrap">
                      {notice.content}
                    </p>
                    <div className="flex items-center gap-3 mt-4 text-[11px] text-foreground-600 sm:hidden">
                      <span>{formatDate(notice.created_at)}</span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-eye-line text-[10px]" />
                        </span>
                        조회 {notice.views}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-1 mt-8">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:text-foreground-700 hover:bg-background-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-s-line" />
              </span>
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`w-8 h-8 flex items-center justify-center text-xs font-bold rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                  currentPage === page
                    ? "bg-primary-500 text-foreground-50"
                    : "text-foreground-600 hover:text-foreground-700 hover:bg-background-200"
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:text-foreground-700 hover:bg-background-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
            </button>
          </div>
        )}

        {/* Empty State */}
        {notices.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
              <span className="w-8 h-8 flex items-center justify-center text-foreground-600">
                <i className="ri-inbox-line text-2xl" />
              </span>
            </div>
            <h2 className="text-lg font-bold text-foreground-700 mb-2">등록된 공지사항이 없습니다</h2>
            <p className="text-sm text-foreground-600">새로운 공지사항이 등록되면 이곳에서 확인하실 수 있습니다</p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}